import { RELAY_HOSTNAME, RELAY_SUBSCRIBE } from "cfg/endpoints";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { Message, jsonSafeToMessage } from "interfaces/relay";

export const useLiveStatus = (
    onMessage: (message: Message) => void,
    {
        abort = false,
        resourceIds = '',  // comma-separated user ids
    }: {abort?: boolean, resourceIds?: string} = {},
) => {
    const socket = useRef<WebSocket | null>(null)
    const connecting = useRef<boolean>(false)
    const [socketClosed, setSocketClosed] = useState<boolean>(false)
    const apiKey = useSelector((state: RootState) => state.authenticationInfo.value?.apiKey)
    const teamId = useSelector((state: RootState) => state.team.value?.team_id)

    useEffect(() => {
        if (!apiKey) return
        if (!teamId) return
        if (abort) return
        connectToSocket()
        return () => {  // close when params change and let auto-reconnect handle it
            socket.current?.close()
        }
    }, [apiKey, teamId, resourceIds, abort])

    const connectToSocket = useCallback(() => {
        if (!apiKey) return
        if (!teamId) return
        if (abort) return
        if (socket.current && socket.current.readyState === WebSocket.OPEN) return
        if (connecting.current) return
        connecting.current = true
        const queryParams = new URLSearchParams({
            team_id: teamId,
        })
        if (resourceIds) {
            queryParams.set('resource_ids', resourceIds)
        }
        const socketUrl = `wss://${RELAY_HOSTNAME}/${RELAY_SUBSCRIBE}?${queryParams.toString()}`
        const ws = socket.current = new WebSocket(socketUrl)
        ws.onopen = () => {
            connecting.current = false
            ws.send(JSON.stringify({'api_key': apiKey}))
            if (socket.current === ws) { backoffMs.current = 1000 }
        }
        ws.onmessage = handleMessage
        ws.onclose = () => {
            if (socket.current === ws) {
                socket.current = null
                connecting.current = false
                setSocketClosed(true)
            }
        }
        setSocketClosed(false)
    }, [apiKey, teamId, resourceIds, abort])

    // reconnect but be polite about it
    const backoffMs = useRef<number>(500)
    const reconnectTimer = useRef<NodeJS.Timeout | null>(null)
    useEffect(() => {
        if (!socketClosed) return
        if (reconnectTimer.current !== null) return
        reconnectTimer.current = setTimeout(() => {
            reconnectTimer.current = null
            if (!socket.current || socket.current.readyState >= WebSocket.CLOSING) {
                backoffMs.current *= 2
                connectToSocket()
            }
        }, backoffMs.current)
    }, [socketClosed])

    const handleMessage = (event: MessageEvent) => {
        const data = JSON.parse(event.data)
        const message = jsonSafeToMessage(data)
        onMessage(message)
    }
}